import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  FileText,
  Search,
  Loader2,
  User,
  Clock,
  ArrowRight,
  Eye,
  Filter,
} from "lucide-react";

import adminService from "@/services/adminService";

const ACTION_COLORS = {
  APPROVE: "bg-green-500/20 text-green-300",
  REJECT: "bg-red-500/20 text-red-300",
  ASSIGN: "bg-blue-500/20 text-blue-300",
  UPDATE: "bg-purple-500/20 text-purple-300",
  VERIFY: "bg-green-500/20 text-green-300",
  REASSIGN: "bg-orange-500/20 text-orange-300",
  UPLOAD: "bg-cyan-500/20 text-cyan-300",
  RESOLVE: "bg-teal-500/20 text-teal-300",
};

const getActionColor = (actionType) => {
  for (const [key, color] of Object.entries(ACTION_COLORS)) {
    if (actionType?.includes(key)) return color;
  }
  return "bg-slate-500/20 text-slate-300";
};

export default function AdminAudit() {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterEntityType, setFilterEntityType] = useState("all");
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);

  useEffect(() => {
    loadAuditLog();
  }, []);

  const loadAuditLog = async () => {
    try {
      setLoading(true);
      const data = await adminService.getAuditLog({ limit: 200 });
      setEntries(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading audit log:", error);
      toast.error("Failed to load audit log");
    } finally {
      setLoading(false);
    }
  };

  const filteredEntries = entries.filter((entry) => {
    const matchesSearch =
      entry.action_type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.entity_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.reason?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesEntityType =
      filterEntityType === "all" || entry.entity_type === filterEntityType;
    return matchesSearch && matchesEntityType;
  });

  // Get unique entity types
  const entityTypes = [...new Set(entries.map((e) => e.entity_type).filter(Boolean))];

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString();
  };

  const formatJson = (obj) => {
    if (!obj) return "N/A";
    return JSON.stringify(obj, null, 2);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-audit">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Audit Log</h1>
          <p className="text-slate-400">
            Complete history of admin actions ({entries.length} entries)
          </p>
        </div>
        <Button
          onClick={loadAuditLog}
          variant="outline"
          className="border-slate-400 text-white bg-slate-700/50 hover:bg-slate-600"
        >
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search actions, entity IDs, reasons..."
            className="pl-10 bg-slate-800 border-slate-700 text-white"
          />
        </div>
        <Select value={filterEntityType} onValueChange={setFilterEntityType}>
          <SelectTrigger className="w-48 bg-slate-800 border-slate-700 text-white">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Entity Type" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700 text-white">
            <SelectItem value="all">All Entities</SelectItem>
            {entityTypes.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Audit Entries Timeline */}
      <div className="space-y-3">
        {filteredEntries.map((entry) => (
          <Card
            key={entry.id}
            className="bg-slate-800/50 border-slate-700 cursor-pointer hover:border-purple-500/50 transition-all"
            onClick={() => {
              setSelectedEntry(entry);
              setShowDrawer(true);
            }}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={getActionColor(entry.action_type)}>
                      {entry.action_type}
                    </Badge>
                    <Badge variant="outline" className="border-slate-600 text-slate-300">
                      {entry.entity_type}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-slate-400">
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {entry.actor_role}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(entry.created_at)}
                    </span>
                  </div>
                  {entry.reason && (
                    <p className="mt-2 text-sm text-slate-300 line-clamp-1">
                      Reason: {entry.reason}
                    </p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-slate-400 hover:text-white"
                >
                  <Eye className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredEntries.length === 0 && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="py-12 text-center text-slate-500">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No audit entries found</p>
          </CardContent>
        </Card>
      )}

      {/* Entry Detail Drawer */}
      <Sheet open={showDrawer} onOpenChange={setShowDrawer}>
        <SheetContent className="bg-slate-800 border-slate-700 text-white w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white">Audit Entry Details</SheetTitle>
          </SheetHeader>

          {selectedEntry && (
            <div className="space-y-6 mt-6">
              {/* Action Badge */}
              <div className="flex gap-2">
                <Badge className={getActionColor(selectedEntry.action_type)}>
                  {selectedEntry.action_type}
                </Badge>
                <Badge variant="outline" className="border-slate-600">
                  {selectedEntry.entity_type}
                </Badge>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-400">Entry ID</p>
                  <p className="text-white font-mono text-sm">{selectedEntry.id}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Entity ID</p>
                  <p className="text-white font-mono text-sm">{selectedEntry.entity_id}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Actor</p>
                  <p className="text-white">
                    {selectedEntry.actor_role}
                    <span className="text-slate-400 text-xs ml-2">
                      ({selectedEntry.actor_user_id?.slice(0, 8)}...)
                    </span>
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Timestamp</p>
                  <p className="text-white">{formatDate(selectedEntry.created_at)}</p>
                </div>
              </div>

              {/* Reason */}
              {selectedEntry.reason && (
                <Card className="bg-slate-700/50 border-slate-600">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-slate-400">Reason</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-white">{selectedEntry.reason}</p>
                  </CardContent>
                </Card>
              )}

              {/* State Changes */}
              <div className="grid grid-cols-1 gap-4">
                {selectedEntry.before_json && (
                  <Card className="bg-slate-700/50 border-slate-600">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-red-400">Before State</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <pre className="text-xs text-slate-300 overflow-auto max-h-48 bg-slate-800 p-3 rounded">
                        {formatJson(selectedEntry.before_json)}
                      </pre>
                    </CardContent>
                  </Card>
                )}

                {selectedEntry.after_json && (
                  <Card className="bg-slate-700/50 border-slate-600">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-green-400">After State</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <pre className="text-xs text-slate-300 overflow-auto max-h-48 bg-slate-800 p-3 rounded">
                        {formatJson(selectedEntry.after_json)}
                      </pre>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Change Comparison */}
              {selectedEntry.before_json && selectedEntry.after_json && (
                <Card className="bg-gradient-to-r from-red-500/10 to-green-500/10 border-slate-600">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-white flex items-center gap-2">
                      State Change Summary
                      <ArrowRight className="w-4 h-4" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4">
                      <div className="flex-1 p-3 bg-red-500/10 rounded border border-red-500/20">
                        <p className="text-xs text-red-400 mb-1">Before</p>
                        <p className="text-white font-mono text-sm">
                          {Object.entries(selectedEntry.before_json || {})
                            .map(([k, v]) => `${k}: ${v}`)
                            .join(", ") || "N/A"}
                        </p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-500 flex-shrink-0" />
                      <div className="flex-1 p-3 bg-green-500/10 rounded border border-green-500/20">
                        <p className="text-xs text-green-400 mb-1">After</p>
                        <p className="text-white font-mono text-sm">
                          {Object.entries(selectedEntry.after_json || {})
                            .map(([k, v]) => `${k}: ${v}`)
                            .join(", ") || "N/A"}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
